/* eslint-disable jsdoc/require-jsdoc */
/** @import {VerticalSiteConfigs} from './verticalSiteConfigs' */

/** @import {VerticalContextValue} from 'lib/types/verticalContext' */
const { verticalSiteConfigs } = require('./verticalSiteConfigs');
const { brandSchema } = require('./types/server/bedrockApi');

const brands = [
  'eonline',
  'news',
  'today',
  'msnbc',
  'telemundo',
];

const newsVerticals = [
  'mach',
  'better',
  'think',
  'globalcitizen',
  'select',
];

const telemundoVerticals = [
  'noticias',
  'deportes',
  'localstationdeportes',
  'shows',
  'entretenimiento',
];

/** @type {Record<import('./types/verticalContext.js').VerticalContextValue, string>} */
const verticalNameMap = {
  better: 'BETTER',
  eonline: 'E! Online',
  globalcitizen: 'Global Citizen',
  mach: 'MACH',
  msnbc: 'MSNBC',
  news: 'NBC News',
  select: 'Select',
  think: 'THINK',
  today: 'TODAY',
  noticias: 'Noticias Telemundo',
  deportes: 'Deportes Telemundo',
  localstationdeportes: 'Deportes Telemundo',
  shows: 'Shows Telemundo',
  entretenimiento: 'Entretenimiento Telemundo',
  telemundo: 'Telemundo',
};

/** @type {Partial<Record<import('./types/verticalContext.js').VerticalContextValue, string>>} */
const verticalSlugMap = {
  better: 'better',
  eonline: 'eonline',
  globalcitizen: 'global-citizen',
  mach: 'mach',
  msnbc: 'msnbc',
  news: 'news',
  select: 'select',
  think: 'think',
  today: 'today',
  noticias: 'noticias',
  telemundo: 'telemundo',
  deportes: 'deportes',
};

const verticalToPath = {
  better: 'nbcnews.com/better',
  globalcitizen: 'nbcnews.com/globalcitizen',
  knowyourvalue: 'nbcnews.com/know-your-value',
  msnbc: 'msnbc.com',
  news: 'nbcnews.com',
  select: 'nbcnews.com/select',
  think: 'nbcnews.com/think',
  today: 'today.com',
};

/** @type {Record<string, string>} */
const verticalHfsIdentifierMap = {
  select: 'select_news',
};

/** @type {Record<string, string>} */
const verticalRouteHfsIdentifierMap = {
  'telemundo/ahora': 'telemundoahora',
};

/** @type {Partial<Record<VerticalContextValue, import('./graphqlTypeDefs.mjs').OrganizationType>>} */
const publisherNameMap = {
  today: 'today',
  msnbc: 'msnbc',
  news: 'nbcnews',
  select: 'nbcnews',
  better: 'nbcnews',
  globalcitizen: 'nbcnews',
  think: 'nbcnews',
  mach: 'nbcnews',
  telemundo: 'telemundo',
  noticias: 'telemundo',
  deportes: 'telemundo',
  shows: 'telemundo',
  entretenimiento: 'telemundo',
};

/**
 * Given a vertical, find the corresponding brand value to use for bedrock.  If
 * the brand doesn't support bedrock, an error will be thrown.
 *
 * @param {VerticalContextValue} vertical
 * @returns {import('./types/server/bedrockApi').Brand}
 */
function getBedrockBrandForVertical(vertical) {
  const publisherName = publisherNameMap[vertical];
  const brand = brandSchema.safeParse(publisherName);

  if (!brand.success) {
    throw new Error(`${publisherName} from vertical ${vertical} is not a supported bedrock brand`);
  }

  return brand.data;
}

const verticals = [
  ...brands,
  ...newsVerticals,
  ...telemundoVerticals,
];


const capitalize = (/** @type {string} */ str) => `${str[0]?.toUpperCase()}${str.slice(1)}`;

const capitalizedVerticals = new Set([...newsVerticals, ...telemundoVerticals].map(capitalize));

const publicoHeadlineBrands = ['think', 'today'];

/**
 * @param {import('./types/verticalContext.js').VerticalContextValue} vertical
 * @returns {string}
 */
const getDisplayNameForVertical = (vertical) => (verticalNameMap[vertical] || '');

/**
 * @param {import('./types/verticalContext.js').VerticalContextValue} vertical
 * @returns {string}
 */
const getSlugForVertical = (vertical) => (verticalSlugMap[vertical] || vertical);

/**
 * Provides the correct HFS identifier for a vertical name
 * @param {string} vertical The vertical name to use to look up the HFS identifier
 * @param {string} path Optional /path to handle vertical and route specific cases
 * @returns {string}
 */
const getHfsIdentifierForVertical = (vertical, path) => verticalRouteHfsIdentifierMap[`${vertical}${path}`]
 || verticalHfsIdentifierMap[vertical]
 || vertical;

/**
 * @param  {string} vertical
 * @returns  {boolean}
 * @deprecated - See https://nbcnewsdigital.atlassian.net/wiki/spaces/BWT/pages/1263829057/Feature+Flags+Env+Vars+and+You
 */
const isTelemundoVertical = (vertical) => [...telemundoVerticals, 'telemundo'].includes(vertical);

/**
 * @param  {string} vertical
 * @returns  {boolean}
 * @deprecated - See https://nbcnewsdigital.atlassian.net/wiki/spaces/BWT/pages/1263829057/Feature+Flags+Env+Vars+and+You
 */
const isNewsVertical = (vertical) => [...newsVerticals, 'news'].includes(vertical);

/**
 * @param {import('./types/verticalContext.js').VerticalContextValue} vertical
 */
const getSiteConfigs = (vertical) => {
  // Cast it so that we can use it as a map
  const verticalSiteConfigMap = /** @type {VerticalSiteConfigs} */ (verticalSiteConfigs);

  // Properties with their own configurations
  if (verticalSiteConfigMap[vertical]) {
    return verticalSiteConfigMap[vertical];
  }
  // Telemundo verticals share Telemundo configuration
  if (['entretenimiento', 'shows'].includes(vertical)) {
    return verticalSiteConfigs.telemundo;
  }
  // Default to News
  return verticalSiteConfigs.news;
};

module.exports = {
  brands,
  capitalizedVerticals,
  getBedrockBrandForVertical,
  getDisplayNameForVertical,
  getHfsIdentifierForVertical,
  getSiteConfigs,
  getSlugForVertical,
  isNewsVertical,
  isTelemundoVertical,
  newsVerticals,
  publicoHeadlineBrands,
  publisherNameMap,
  telemundoVerticals,
  verticalNameMap,
  verticals,
  verticalSlugMap,
  verticalToPath,
};
